/**
 * Dashboard Error Boundary
 * Catches errors in dashboard components and displays user-friendly error UI
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Home,
  ChevronRight,
  AlertCircle,
  XCircle
} from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  dashboardName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

class DashboardErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console (could also send to error tracking service)
    console.error('Dashboard Error Boundary caught an error:', error, errorInfo);

    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);

    // Log to external error tracking service if needed
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo, errorCount } = this.state;
      const { dashboardName = 'Dashboard' } = this.props;

      // Check if this is a critical error (multiple consecutive errors)
      const isCritical = errorCount > 2;

      return (
        <div className="min-h-screen bg-bg-muted flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            {/* Error Card */}
            <div className="bg-white rounded-lg shadow-lg border border-border-default overflow-hidden">
              {/* Header */}
              <div className={`p-6 ${isCritical ? 'bg-ds-status-error-light' : 'bg-ds-status-warning-light'}`}>
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-full ${isCritical ? 'bg-ds-status-error-main' : 'bg-ds-status-warning-main'}`}>
                    {isCritical ? (
                      <XCircle className="w-8 h-8 text-white" />
                    ) : (
                      <AlertTriangle className="w-8 h-8 text-white" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h1 className={`text-2xl font-bold ${isCritical ? 'text-ds-status-error-dark' : 'text-ds-status-warning-dark'}`}>
                      {isCritical ? 'Critical Error' : 'Something Went Wrong'}
                    </h1>
                    <p className={`mt-2 text-sm ${isCritical ? 'text-ds-status-error-main' : 'text-ds-status-warning-main'}`}>
                      {isCritical
                        ? `The ${dashboardName} has encountered multiple errors and cannot recover automatically.`
                        : `An error occurred while loading the ${dashboardName}. Don't worry, your data is safe.`
                      }
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Details */}
              <div className="p-6 space-y-4">
                {/* Error Message */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Error Message
                  </h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <code className="text-sm text-ds-status-error-dark break-words">
                      {error?.message || 'Unknown error occurred'}
                    </code>
                  </div>
                </div>

                {/* Stack Trace (Development Only) */}
                {process.env.NODE_ENV === 'development' && errorInfo && (
                  <details className="group">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 flex items-center">
                      <ChevronRight className="w-4 h-4 mr-1 transition-transform group-open:rotate-90" />
                      Technical Details (Development Mode)
                    </summary>
                    <div className="mt-3 bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
                      <pre className="text-xs whitespace-pre-wrap">
                        {error?.stack}
                        {'\n\n'}
                        Component Stack:
                        {errorInfo.componentStack}
                      </pre>
                    </div>
                  </details>
                )}

                {/* Error Count Warning */}
                {errorCount > 1 && (
                  <div className="bg-ds-status-warning-light border border-ds-status-warning-main rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="w-5 h-5 text-ds-status-warning-dark mr-3 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-ds-status-warning-dark mb-1">
                          Repeated Error Detected
                        </h4>
                        <p className="text-sm text-ds-status-warning-main">
                          This error has occurred {errorCount} times. If the problem persists after reloading,
                          please contact support.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Suggestions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">What can you do?</h4>
                  <ul className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Try refreshing the page or going back to the home page</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Check your internet connection</span>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <span>Clear your browser cache and cookies</span>
                    </li>
                    {isCritical && (
                      <li className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>Contact technical support if the issue persists</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              {/* Actions */}
              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={this.handleReset}
                  className="flex-1 px-4 py-2 bg-ds-primary-600 text-white rounded-lg hover:bg-ds-primary-700 transition-colors flex items-center justify-center space-x-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Try Again</span>
                </button>

                {isCritical ? (
                  <button
                    onClick={this.handleReload}
                    className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span>Reload Page</span>
                  </button>
                ) : (
                  <button
                    onClick={this.handleGoHome}
                    className="flex-1 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Home className="w-4 h-4" />
                    <span>Go Home</span>
                  </button>
                )}
              </div>
            </div>

            {/* Help Text */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Need help?{' '}
                <a
                  href="mailto:support@example.com"
                  className="text-ds-primary-600 hover:text-ds-primary-700 underline"
                >
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default DashboardErrorBoundary;

/**
 * Hook to use error boundary programmatically
 */
export const useDashboardErrorHandler = () => {
  const handleError = (error: Error, errorInfo?: ErrorInfo) => {
    console.error('Dashboard Error:', error, errorInfo);
    // Could send to error tracking service
  };

  return { handleError };
};
